/**
 * Human labels for admin setup screens: settings registry keys, permission
 * keys and assorted codes that should never be shown raw to office admins.
 */
import { humanize } from './format.ts';

// ─── Settings registry ──────────────────────────────────────────────────────

export interface SettingMeta {
  label: string;
  help?: string;
  unit?: string;
}

export const SETTING_META: Record<string, SettingMeta> = {
  'company.legal_name': { label: 'Legal name' },
  'company.trading_name': { label: 'Trading name' },
  'company.tin': { label: 'TIN' },
  'company.billing_address': { label: 'Billing address' },
  'company.hub_latitude': { label: 'Hub latitude' },
  'company.hub_longitude': { label: 'Hub longitude' },

  'approvals.exception_threshold_peso': {
    label: 'Exception approval threshold',
    help: 'Exceptions above this amount need an approver with "approve any exception".',
    unit: '₱',
  },
  'approvals.cost_threshold_peso': {
    label: 'Cost approval threshold',
    help: 'Trip costs above this amount need an approver with "approve any cost".',
    unit: '₱',
  },
  'approvals.delegated_approver_id': { label: 'Delegated approver', help: 'Person who approves when the usual approver is away.' },

  'booking.min_lead_time_hours': {
    label: 'Minimum booking lead time',
    help: 'How far ahead clients must book a trip.',
    unit: 'hours',
  },
  'booking.allow_off_map_quotes': { label: 'Allow quotes for addresses outside service zones' },
  'booking.credit_check_enabled': { label: 'Check client credit limit when booking' },

  'cancellation.bands': { label: 'Cancellation charge bands', help: 'Notice period (hours) and the % of the trip charged.' },

  'scanning.require_manifest': { label: 'Require a manifest before scanning' },
  'scanning.offline_grace_minutes': { label: 'Offline scanning grace period', unit: 'minutes' },

  'rating.waiting_free_minutes': { label: 'Free waiting time', help: 'Waiting before charges start.', unit: 'minutes' },
  'rating.waiting_increment_minutes': { label: 'Waiting time billing increment', unit: 'minutes' },
  'rating.waiting_cap_minutes_per_location_per_day': {
    label: 'Maximum billable waiting per location per day',
    unit: 'minutes',
  },
  'rating.margin_floor_pct': { label: 'Minimum margin', help: 'Quotes below this margin are flagged.', unit: '%' },
  'rating.additional_drop_default': { label: 'Default additional drop charge', unit: '₱' },

  'billing.short_payment_tolerance_pct': { label: 'Short payment tolerance', unit: '%' },
  'billing.vat_rate': { label: 'VAT rate', help: 'As a decimal, e.g. 0.12 for 12%.' },
  'billing.auto_rate_on_complete': { label: 'Price trips automatically when completed' },
  'billing.ready_to_bill_gates': { label: 'Ready-to-bill checks', help: 'Which checks must pass before a trip can be billed.' },

  'crew.session_hours': { label: 'Crew login session length', unit: 'hours' },
  'crew.show_earnings': { label: 'Show crew their earnings' },

  'tracking.enabled': {
    label: 'Share truck locations from the crew app',
    help: 'While crew are clocked in or on a trip, the crew app sends the truck location for the live map.',
  },
  'tracking.interval_seconds': {
    label: 'Location update interval',
    help: 'How often the crew app records the truck location. Shorter uses more battery and data.',
    unit: 'seconds',
  },
  'tracking.retention_days': {
    label: 'Keep location history for',
    help: 'Older truck locations are deleted automatically every day.',
    unit: 'days',
  },

  'fleet.fuel_leakage_pct': { label: 'Fuel leakage alert threshold', unit: '%' },
  'fleet.low_toll_balance': { label: 'Low toll card balance alert', unit: '₱' },
  'fleet.hub_geofence_m': {
    label: 'Hub radius for attendance',
    help: 'Clock-ins further than this from the hub are flagged "Away from hub" in the attendance log. Crew can clock in anywhere — this never blocks.',
    unit: 'metres',
  },
  'fleet.blocking_doc_types': { label: 'Documents that block dispatch when expired' },
  'fleet.subcon_variance_pct': { label: 'Subcontractor bill variance alert', unit: '%' },
  'fleet.subcon_variance_min_peso': { label: 'Subcontractor bill variance minimum', unit: '₱' },

  'commission.require_validated_costs': { label: 'Require validated trip costs before commission is earned' },

  'sales.prospect_cap': { label: 'Maximum open prospects per sales agent' },
  'sales.prospect_window_days': { label: 'Prospect ownership window', unit: 'days' },

  'notifications.email_from': { label: 'Default sender email address' },
  'notifications.smtp_from': { label: 'Sender email address (old setting)', help: 'No longer used — set "Default sender email address" instead.' },
  'notifications.digest_hour': { label: 'Daily digest send time', help: 'Hour of the day (0–23, Manila time).' },
  'notifications.suppress_delivery': { label: 'Pause outgoing email, SMS and push (in-app only)' },
  'notifications.transport': { label: 'Notification delivery mode', help: 'live, test, or in_app_only.' },

  'retention.held_cargo_ageing_days': { label: 'Held cargo ageing alert', unit: 'days' },
  'retention.audit_log_days': { label: 'Keep audit log for', unit: 'days' },
  'retention.pod_archive_days': { label: 'Keep proof of delivery for', unit: 'days' },
  'storage.lifecycle_days': { label: 'Keep photos and receipts for', unit: 'days' },

  'pay.vale_enabled': { label: 'Allow cash advances (vale)' },
  'pay.vale_cap_per_person': { label: 'Cash advance limit per person', unit: '₱' },
  'pay.classification_threshold': { label: 'Long-haul trip threshold (selling price)', unit: '₱' },
  'pay.driver_mnl_rate': { label: 'Driver rate — Metro Manila (6W)', unit: '₱' },
  'pay.driver_crk_rate': { label: 'Driver rate — long haul (6W)', unit: '₱' },
  'pay.helper_mnl_rate': { label: 'Helper rate — Metro Manila (6W)', unit: '₱' },
  'pay.helper_crk_rate': { label: 'Helper rate — long haul (6W)', unit: '₱' },
  'pay.driver_10w_mnl_rate': { label: 'Driver rate — Metro Manila (10W)', unit: '₱' },
  'pay.driver_10w_crk_rate': { label: 'Driver rate — long haul (10W)', unit: '₱' },
  'pay.helper_10w_mnl_rate': { label: 'Helper rate — Metro Manila (10W)', unit: '₱' },
  'pay.helper_10w_crk_rate': { label: 'Helper rate — long haul (10W)', unit: '₱' },

  'feature.multi_client_consolidation': { label: 'Allow several clients on one trip' },
  'feature.vale_enabled': { label: 'Cash advances feature' },

  'maps.provider': { label: 'Maps provider', help: 'google, mapbox, or manual.' },
  'maps.mapbox_public_token': { label: 'Mapbox public token' },
  'maps.mapbox_secret_token': { label: 'Mapbox secret token' },
  'maps.style_url': { label: 'Mapbox map style' },
  'maps.monthly_ceiling': { label: 'Monthly maps usage limit' },
};

/** Human label for a settings key; unknown keys fall back to a humanised last segment. */
export function settingLabel(key: string): string {
  const meta = SETTING_META[key];
  if (meta) return meta.label;
  const last = key.split('.').pop() ?? key;
  return humanize(last);
}

const SETTING_DOMAIN_LABELS: Record<string, string> = {
  company: 'Company',
  approvals: 'Approvals',
  booking: 'Booking',
  cancellation: 'Cancellations',
  scanning: 'Scanning',
  rating: 'Pricing',
  billing: 'Billing',
  crew: 'Crew',
  tracking: 'Live truck tracking',
  fleet: 'Fleet',
  ops: 'Operations',
  commission: 'Commission',
  sales: 'Sales',
  notifications: 'Notifications',
  retention: 'Data retention',
  storage: 'File storage',
  pay: 'Crew pay',
  maps: 'Maps',
};

export function settingDomainLabel(domain: string): string {
  return SETTING_DOMAIN_LABELS[domain] ?? humanize(domain);
}

// ─── Permissions ────────────────────────────────────────────────────────────

export const PERMISSION_LABELS: Record<string, string> = {
  'settings.manage': 'Manage system settings',
  'integrations.manage': 'Manage integrations',
  'roles.manage': 'Edit roles and permissions',
  'users.manage': 'Manage user accounts',
  'audit.view': 'View audit log',
  'trips.book': 'Book trips',
  'trips.confirm': 'Confirm or decline trips',
  'trips.dispatch': 'Dispatch trips',
  'trips.resequence': 'Reorder stops',
  'trips.correct': 'Correct trip status and times',
  'trips.view_all': 'View all trips',
  'exceptions.approve': 'Approve exceptions up to the threshold',
  'exceptions.approve_all': 'Approve any exception',
  'costs.approve': 'Approve costs up to the threshold',
  'costs.approve_all': 'Approve any cost',
  'billing.run': 'Run billing',
  'billing.push_xero': 'Send invoices to Xero',
  'pay.run': 'Run crew payouts',
  'commission.settle': 'Settle commissions',
  'payables.manage': 'Manage payables',
  'margin.view': 'See margins and costs',
  'fleet.manage': 'Manage vehicles and subcontractors',
  'documents.manage': 'Manage documents',
  'maintenance.manage': 'Manage maintenance schedules',
  'rates.manage': 'Edit rate cards',
  'rates.activate': 'Activate rate cards',
  'pay_cards.manage': 'Edit pay cards',
  'clients.manage': 'Manage clients',
  'clients.approve': 'Approve new clients',
  'clients.credit_override': 'Override credit limit blocks',
  'crew.execute': 'Run assigned trips',
  'crew.attendance': 'Clock in and out',
  'crew.cost_log': 'Log trip costs',
  'crew.earnings_view': 'See own earnings',
  'sales.submit_account': 'Submit new accounts',
  'sales.view_own': 'See own accounts and commission',
  'sales.manage_plans': 'Manage commission plans',
  'portal.book': 'Book trips in the client portal',
  'portal.cancel': 'Cancel trips in the client portal',
  'portal.view_trips': "See their company's trips",
  'portal.download_docs': 'Download proof of delivery',
  'portal.manage_users': "Manage their company's users",
};

/** Group headings in display order, keyed by permission prefix. */
const PERMISSION_GROUPS: Array<{ id: string; heading: string; prefixes: string[] }> = [
  { id: 'admin', heading: 'Administration', prefixes: ['settings', 'integrations', 'roles', 'users', 'audit'] },
  { id: 'dispatch', heading: 'Trips and dispatch', prefixes: ['trips', 'exceptions', 'costs'] },
  { id: 'finance', heading: 'Finance', prefixes: ['billing', 'pay', 'commission', 'payables', 'margin'] },
  { id: 'fleet', heading: 'Fleet', prefixes: ['fleet', 'documents', 'maintenance'] },
  { id: 'pricing', heading: 'Rate and pay cards', prefixes: ['rates', 'pay_cards'] },
  { id: 'clients', heading: 'Clients', prefixes: ['clients'] },
  { id: 'crew', heading: 'Crew app', prefixes: ['crew'] },
  { id: 'sales', heading: 'Sales', prefixes: ['sales'] },
  { id: 'portal', heading: 'Client portal', prefixes: ['portal'] },
];

export function permissionLabel(key: string): string {
  return PERMISSION_LABELS[key] ?? humanize(key.replace('.', ' '));
}

export interface PermissionGroup {
  id: string;
  heading: string;
  keys: string[];
}

/** Group permission keys by prefix, in a stable, human order. Unknown prefixes go under "Other". */
export function groupPermissions(keys: string[]): PermissionGroup[] {
  const groups: PermissionGroup[] = PERMISSION_GROUPS.map((g) => ({ id: g.id, heading: g.heading, keys: [] }));
  const other: PermissionGroup = { id: 'other', heading: 'Other', keys: [] };
  for (const key of keys) {
    const prefix = key.split('.')[0];
    const idx = PERMISSION_GROUPS.findIndex((g) => g.prefixes.includes(prefix));
    (idx >= 0 ? groups[idx] : other).keys.push(key);
  }
  const order = Object.keys(PERMISSION_LABELS);
  const rank = (k: string) => {
    const i = order.indexOf(k);
    return i === -1 ? Number.MAX_SAFE_INTEGER : i;
  };
  return [...groups, other]
    .filter((g) => g.keys.length > 0)
    .map((g) => ({ ...g, keys: [...g.keys].sort((a, b) => rank(a) - rank(b) || a.localeCompare(b)) }));
}

/** Permissions an Admin role must not lose without a warning — losing them can lock everyone out. */
export const LOCKOUT_PERMISSIONS = ['settings.manage', 'roles.manage'];

// ─── Assorted codes ─────────────────────────────────────────────────────────

function lookup(map: Record<string, string>, value: string | null | undefined): string {
  if (!value) return '';
  return map[value] ?? humanize(value);
}

const VEHICLE_STATUS_LABELS: Record<string, string> = {
  available: 'Available',
  on_trip: 'On a trip',
  maintenance: 'In maintenance',
  unavailable: 'Unavailable',
  inactive: 'Inactive',
  retired: 'Retired',
};
export const vehicleStatusLabel = (v: string | null | undefined) => lookup(VEHICLE_STATUS_LABELS, v);

const OWNERSHIP_LABELS: Record<string, string> = { owned: 'Company-owned', subcon: 'Subcontracted' };
export const ownershipLabel = (v: string | null | undefined) => lookup(OWNERSHIP_LABELS, v);

const BILLING_PERIOD_LABELS: Record<string, string> = {
  per_trip: 'Per trip',
  weekly: 'Weekly',
  fortnightly: 'Fortnightly',
  semi_monthly: 'Twice a month',
  monthly: 'Monthly',
};
export const billingPeriodLabel = (v: string | null | undefined) => lookup(BILLING_PERIOD_LABELS, v);

const TIER_LABELS: Record<string, string> = {
  act_now: 'Act now',
  awareness: 'Awareness',
  digest: 'Daily digest',
};
export const notificationTierLabel = (v: string | null | undefined) => lookup(TIER_LABELS, v);

const NOTIFICATION_TYPE_LABELS: Record<string, string> = {
  'trip.exception_filed': 'Trip exception filed',
  'trip.rtb_pending': 'Trip waiting to be billed',
  'trip.quote_ready': 'Quote ready',
  'held_cargo.aged': 'Held cargo ageing',
  'billing.push_failed': 'Invoice failed to send to Xero',
  'portal.user_invite': 'Client portal invitation',
  'auth.password_reset': 'Password set by administrator',
  'auth.password_reset_link': 'Password reset link',
  'crew.trip_assigned': 'Crew: new trip assigned',
  'crew.trip_changed': 'Crew: trip updated',
  'crew.trip_cancelled': 'Crew: trip cancelled',
  'crew.run_changed': 'Crew: trip order changed',
};
export function notificationTypeLabel(v: string | null | undefined): string {
  if (!v) return '';
  return NOTIFICATION_TYPE_LABELS[v] ?? humanize(v.replace(/\./g, ' '));
}

const DELIVERY_RESULT_LABELS: Record<string, string> = {
  queued_stub: 'Not sent (delivery paused)',
  deferred_quiet_hours: 'Deferred (quiet hours)',
  sent: 'Sent',
  delivered: 'Delivered',
  failed: 'Failed',
  skipped: 'Skipped',
  queued: 'Queued',
  suppressed: 'Not sent (delivery paused)',
  no_devices: 'No phone with notifications on',
};
export const deliveryResultLabel = (v: string | null | undefined) => lookup(DELIVERY_RESULT_LABELS, v);

const CHANNEL_LABELS: Record<string, string> = { push: 'Push', sms: 'SMS', email: 'Email', in_app: 'In-app' };
export const channelLabel = (v: string | null | undefined) => lookup(CHANNEL_LABELS, v);

const CHARGE_TYPE_LABELS: Record<string, string> = {
  additional_drop: 'Additional drop',
  waiting_time: 'Waiting time',
  tolls: 'Tolls',
  fuel_adjustment: 'Fuel adjustment',
  cancellation: 'Cancellation fee',
  return_leg: 'Return trip',
  accessorial: 'Other extra service',
  manual: 'Manual charge',
};
export const chargeTypeLabel = (v: string | null | undefined) => lookup(CHARGE_TYPE_LABELS, v);

const CHARGE_UNIT_LABELS: Record<string, string> = {
  each: 'Each time',
  minute: 'Per minute',
  hour: 'Per hour',
  drop: 'Per drop',
  trip: 'Per trip',
  km: 'Per km',
};
export const chargeUnitLabel = (v: string | null | undefined) => lookup(CHARGE_UNIT_LABELS, v);
